import { COLORS } from "../shared/constants";
import { IconSearch, IconChevron, IconBack, IconX, IconEdit, IconLock, IconCopy, IconTrash, IconPlus } from "../shared/icons";
import { formatDuration } from "../shared/utils";
import { useState } from "react";
import * as api from "../api";

export default // ─── Protocol List ───────────────────────────────────────────
function ProtocolList({ protocols, currentUser, onNavigate, onCopy, onDelete, onEdit, onNew, embedded, onReorder }) {
  const [sortField, setSortField] = useState("sort_order");
  const [sortDir, setSortDir] = useState("asc");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [reordering, setReordering] = useState(false);

  const handleMove = async (protocol, direction) => {
    // Get active protocols sorted by current sort_order
    const sorted = [...protocols].filter(p => p.is_active !== false).sort((a, b) => (a.sort_order ?? 100) - (b.sort_order ?? 100));
    const idx = sorted.findIndex(p => p.id === protocol.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    // Swap sort_orders
    const newOrder = sorted.map((p, i) => ({ ...p }));
    const tempOrder = newOrder[idx].sort_order ?? (idx * 10 + 10);
    newOrder[idx].sort_order = newOrder[swapIdx].sort_order ?? (swapIdx * 10 + 10);
    newOrder[swapIdx].sort_order = tempOrder;

    // If they have the same sort_order, assign sequential values
    if (newOrder[idx].sort_order === newOrder[swapIdx].sort_order) {
      newOrder.forEach((p, i) => { p.sort_order = (i + 1) * 10; });
    }

    const order = newOrder.map(p => ({ id: p.id, sort_order: p.sort_order }));
    setReordering(true);
    try {
      await api.reorderProtocols(order);
      if (onReorder) onReorder(order);
    } catch (err) { console.error("Reorder:", err); }
    setReordering(false);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };

  const filtered = protocols
    .filter((p) => {
      if (typeFilter !== "ALL" && p.treatment_type !== typeFilter) return false;
      if (search) {
        const s = search.toLowerCase();
        return p.name.toLowerCase().includes(s);
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "name": aVal = a.name.toLowerCase(); bVal = b.name.toLowerCase(); break;
        case "treatment_type": aVal = a.treatment_type; bVal = b.treatment_type; break;
        case "frequency_hz": aVal = a.frequency_hz; bVal = b.frequency_hz; break;
        case "treatment_ratio_pct": aVal = a.treatment_ratio_pct; bVal = b.treatment_ratio_pct; break;
        case "total_pulses": aVal = a.total_pulses; bVal = b.total_pulses; break;
        case "approx_duration_sec": aVal = a.approx_duration_sec; bVal = b.approx_duration_sec; break;
        case "fda_approved": aVal = a.fda_approved ? 1 : 0; bVal = b.fda_approved ? 1 : 0; break;
        case "sort_order": aVal = a.sort_order ?? 100; bVal = b.sort_order ?? 100; break;
        default: aVal = a.sort_order ?? 100; bVal = b.sort_order ?? 100;
      }
      if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const SortHeader = ({ field, label, width }) => (
    <th
      onClick={() => handleSort(field)}
      style={{
        padding: "12px 14px", textAlign: "left", fontSize: "12px", fontWeight: "600",
        color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px",
        cursor: "pointer", userSelect: "none", borderBottom: `2px solid ${COLORS.border}`,
        width: width || "auto", whiteSpace: "nowrap",
        background: sortField === field ? COLORS.primaryLight : "transparent",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
        {label}
        {sortField === field && <IconChevron dir={sortDir === "asc" ? "up" : "down"} />}
      </span>
    </th>
  );

  return (
    <div style={embedded ? {} : { padding: "24px", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px", gap: "16px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {!embedded && (
            <button onClick={() => onNavigate("HOME")} style={{ background: "none", border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "8px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textSecondary }}>
              <IconBack />
            </button>
          )}
          <h2 style={{ color: COLORS.text, fontSize: embedded ? "16px" : "20px", fontWeight: "600", margin: 0 }}>
            Stimulation Protocols
            <span style={{ color: COLORS.textSecondary, fontWeight: "400", fontSize: "14px", marginLeft: "8px" }}>({filtered.length})</span>
          </h2>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: "8px", padding: "0 12px", height: "40px", width: "220px" }}>
            <IconSearch />
            <input type="text" placeholder="Search protocols…" value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ border: "none", outline: "none", background: "transparent", marginLeft: "8px", fontSize: "14px", width: "100%", color: COLORS.text }} />
            {search && <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: COLORS.textSecondary }}><IconX /></button>}
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            style={{ height: "40px", padding: "0 12px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: "13px", color: COLORS.text, cursor: "pointer" }}>
            <option value="ALL">All Types</option>
            <option value="RTMS">rTMS</option>
            <option value="TBS">Theta Burst</option>
            <option value="MPNS">mPNS</option>
          </select>
          <button onClick={onNew}
            style={{ height: "40px", padding: "0 18px", borderRadius: "8px", border: "none", background: COLORS.accent, color: "#FFF", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" }}>
            <IconPlus /> New Protocol
          </button>
        </div>
      </div>

      <div style={embedded ? { overflowX: "auto" } : { background: COLORS.surface, borderRadius: "12px", border: `1px solid ${COLORS.borderLight}`, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {embedded && (
                  <th style={{ padding: "12px 8px", borderBottom: `2px solid ${COLORS.border}`, width: "50px", fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px", textAlign: "center" }}>Order</th>
                )}
                <SortHeader field="name" label="Protocol Name" />
                <SortHeader field="treatment_type" label="Type" width="90px" />
                <SortHeader field="treatment_ratio_pct" label="Intensity" width="100px" />
                <SortHeader field="frequency_hz" label="Freq (Hz)" width="90px" />
                <SortHeader field="total_pulses" label="Pulses" width="80px" />
                <SortHeader field="approx_duration_sec" label="Duration" width="90px" />
                <SortHeader field="fda_approved" label="FDA" width="60px" />
                <th style={{ padding: "12px 14px", borderBottom: `2px solid ${COLORS.border}`, width: "180px", fontSize: "12px", fontWeight: "600", color: COLORS.textSecondary, textTransform: "uppercase", letterSpacing: "0.5px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={embedded ? 9 : 8} style={{ padding: "40px", textAlign: "center", color: COLORS.textSecondary }}>No protocols found.</td></tr>
              ) : filtered.map((p, idx) => (
                <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.primaryLight)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                  {embedded && (
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                        <button onClick={() => handleMove(p, "up")} disabled={idx === 0 || reordering}
                          style={{ width: "24px", height: "20px", borderRadius: "4px", border: `1px solid ${COLORS.borderLight}`, background: "transparent", color: idx === 0 ? COLORS.borderLight : COLORS.textSecondary, fontSize: "10px", cursor: idx === 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: idx === 0 ? 0.3 : 1 }}>
                          ▲
                        </button>
                        <button onClick={() => handleMove(p, "down")} disabled={idx === filtered.length - 1 || reordering}
                          style={{ width: "24px", height: "20px", borderRadius: "4px", border: `1px solid ${COLORS.borderLight}`, background: "transparent", color: idx === filtered.length - 1 ? COLORS.borderLight : COLORS.textSecondary, fontSize: "10px", cursor: idx === filtered.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: idx === filtered.length - 1 ? 0.3 : 1 }}>
                          ▼
                        </button>
                      </div>
                    </td>
                  )}
                  <td style={{ padding: "14px", fontSize: "14px", fontWeight: "500", color: COLORS.text }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                      {p.is_system_default && <span style={{ color: COLORS.textSecondary }} title="System default (read-only)"><IconLock /></span>}
                      {p.name}
                    </span>
                  </td>
                  <td style={{ padding: "14px" }}>
                    <span style={{
                      padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "600",
                      background: p.treatment_type === "TBS" ? "#EDE7F6" : p.treatment_type === "MPNS" ? "#FEF3C7" : COLORS.primaryLight,
                      color: p.treatment_type === "TBS" ? "#6A3DB5" : p.treatment_type === "MPNS" ? "#92400E" : COLORS.primary,
                    }}>
                      {p.treatment_type === "TBS" ? "TBS" : p.treatment_type === "MPNS" ? "mPNS" : "rTMS"}
                    </span>
                  </td>
                  <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>
                    {p.treatment_type === "MPNS"
                      ? `${p.default_intensity_pct ?? p.treatment_ratio_pct}% machine`
                      : p.intensity_is_machine_pct ? `${p.treatment_ratio_pct}% machine` : `${p.treatment_ratio_pct}% MT`
                    }
                  </td>
                  <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>{p.frequency_hz}</td>
                  <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>{p.total_pulses.toLocaleString()}</td>
                  <td style={{ padding: "14px", fontSize: "13px", color: COLORS.textSecondary }}>{formatDuration(p.approx_duration_sec)}</td>
                  <td style={{ padding: "14px", textAlign: "center" }}>
                    {p.fda_approved ? (
                      <span style={{ color: COLORS.accent, fontWeight: "700", fontSize: "12px" }}>✓</span>
                    ) : (
                      <span style={{ color: COLORS.textSecondary, fontSize: "12px" }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: "4px", flexWrap: "wrap", alignItems: "center" }}>
                      <button onClick={() => onCopy(p)} title="Duplicate" style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}>
                        <IconCopy /> Copy
                      </button>
                      {!p.is_system_default && (
                        <>
                          <button onClick={() => onEdit(p)} title="Edit" style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.primary, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}>
                            <IconEdit /> Edit
                          </button>
                          {confirmDeleteId === p.id ? (
                            <div style={{ display: "flex", gap: "3px", alignItems: "center" }}>
                              <span style={{ fontSize: "11px", color: COLORS.danger, fontWeight: "600", whiteSpace: "nowrap" }}>Delete?</span>
                              <button onClick={() => { onDelete(p.id); setConfirmDeleteId(null); }}
                                style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: "none", background: COLORS.danger, color: "#FFF", fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                                Yes
                              </button>
                              <button onClick={() => setConfirmDeleteId(null)}
                                style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textSecondary, fontSize: "11px", fontWeight: "600", cursor: "pointer" }}>
                                No
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmDeleteId(p.id)} title="Delete" style={{ height: "28px", padding: "0 8px", borderRadius: "6px", border: `1px solid ${COLORS.dangerBorder}`, background: COLORS.dangerLight, color: COLORS.danger, fontSize: "11px", fontWeight: "600", cursor: "pointer", display: "flex", alignItems: "center", gap: "3px" }}>
                              <IconTrash />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Protocol Form (Create / Edit) ──────────────────────────

